import { useWorkspaceConnections } from "@web-app/api";
import {
  AccordionV2,
  HighlightString,
  OrderCard,
  Skeleton,
} from "@web-app/components";
import { focusOnOrder } from "@web-app/pages/common/DispatchMap/mapFocusUtils";
import useDispatchStore from "@web-app/store/dispatchStore";
import { BaseApp, Order, Route, User } from "@web-app/types";

import useLocationsView from "./useLocationsView";

export default function LocationsView({
  appsMap,
  assigneesMap,
  autoAssignSearchingIds,
  isAppsLoading,
  isAssigneesLoading,
  isLoadingOrders,
  onSelectExternalProvider,
  onShare,
  routedOrdersMap,
  searchTerm,
  unroutedOrders,
}: {
  appsMap: Map<string, BaseApp>;
  assigneesMap: Map<string, User>;
  autoAssignSearchingIds: Set<string>;
  isAppsLoading?: boolean;
  isAssigneesLoading?: boolean;
  isLoadingOrders: boolean;
  onSelectExternalProvider: (
    entity: Order | Route | null,
    provider: string,
    type: string,
  ) => void;
  onShare?: (orderId: string, connectedWorkspaceId: string) => void;
  routedOrdersMap: Map<string, Order>;
  searchTerm: string;
  unroutedOrders: Order[];
}) {
  const currentUser = useDispatchStore((state) => state.user);
  const sortParameter = useDispatchStore((state) => state.sortParameter);
  const { groupedOrderLocations } = useLocationsView({
    routedOrdersMap,
    searchTerm,
    sortParameter,
    unroutedOrders,
  });
  const { data: workspaceConnections } = useWorkspaceConnections();

  if (isLoadingOrders) {
    return (
      <div className="flex flex-col px-4">
        <div className="flex flex-col gap-2 mt-2">
          <Skeleton className="" height="h-[40px] lg:h-[61px]" width="w-full" />
          <Skeleton className="" height="h-[40px] lg:h-[61px]" width="w-full" />
          <Skeleton className="" height="h-[40px] lg:h-[61px]" width="w-full" />
          <Skeleton
            height="h-[40px] lg:h-[61px]"
            width="w-full"
            className=" bg-gradient-to-b from-gray-200 !bg-white to-gray-50"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col px-4 pb-20 relative z-10 overflow-y-auto">
      {Array.from(groupedOrderLocations.entries()).map(
        ([locationName, orders]) => (
          <AccordionV2 key={locationName} isBorderless>
            <AccordionV2.Label>
              <div className="flex flex-col items-start">
                <span className="text-gray-700 font-normal text-xs lg:text-sm">
                  <HighlightString
                    text={locationName}
                    searchTerm={searchTerm}
                  />
                </span>
                <span className="text-gray-400 text-[10px] lg:text-xs font-normal">
                  {orders.length} Orders
                </span>
              </div>
            </AccordionV2.Label>
            <AccordionV2.Content>
              <div className="flex flex-col gap-2 mt-1 mb-2">
                {orders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    app={order.appId ? appsMap.get(order.appId) : null}
                    isAppsLoading={isAppsLoading}
                    assignee={
                      order.assigneeId
                        ? assigneesMap.get(order.assigneeId)
                        : null
                    }
                    isAssigneesLoading={isAssigneesLoading}
                    isAutoAssignSearching={autoAssignSearchingIds.has(
                      order.routeId || order.id,
                    )}
                    searchTerm={searchTerm}
                    workspaceConnections={workspaceConnections || []}
                    onViewDetails={() => {
                      useDispatchStore.setState({
                        focusedOrderId: order.id,
                        mapFocusedOrderId: order.id,
                      });

                      setTimeout(() => {
                        const { dispatchMapRef } = useDispatchStore.getState();
                        if (dispatchMapRef?.current) {
                          focusOnOrder({
                            mapRef: dispatchMapRef.current,
                            order,
                          });
                        }
                      }, 0);
                    }}
                    onShare={
                      currentUser?.currentWorkspaceId === order.workspaceId &&
                      onShare
                        ? (connectedWorkspaceId) =>
                            onShare(order.id, connectedWorkspaceId)
                        : undefined
                    }
                    onSelectExternalProvider={onSelectExternalProvider}
                  />
                ))}
              </div>
            </AccordionV2.Content>
          </AccordionV2>
        ),
      )}
    </div>
  );
}
